/**
 * Filtro "Segmento" como dimensão (Marketing Hub v3 · W3, item 4a) — o
 * mesmo bloco `segment` que a resposta do Dashboard, do desempenho de
 * campanha e das métricas da régua ganham quando `segment_id` é passado.
 * Lógica pura: a leitura da resposta (dual grafia, backend em paralelo) e
 * a frase que as três telas mostram.
 */
import type { SegmentFilterResult } from '../types/segments';

type Json = Record<string, unknown>;

function asJson(v: unknown): Json {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {};
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pick(r: Json, camel: string, snake: string): unknown {
  return r[camel] !== undefined ? r[camel] : r[snake];
}

/**
 * `null` quando a resposta não trouxe o bloco `segment` — ausência de
 * `segment_id` no pedido, ambiente sem a leitura publicada, ou (dark) a
 * flag `MKT_SEGMENTS_V2_ENABLED` desligada. Nunca lançado como erro: a
 * tela apenas não mostra o resumo do filtro.
 */
export function normalizarFiltroDeSegmento(raw: unknown): SegmentFilterResult | null {
  if (raw === null || raw === undefined) return null;
  const r = asJson(raw);
  const id = str(r.id);
  if (!id) return null;
  return {
    id,
    name: str(r.name) ?? '',
    membersActive: num(pick(r, 'membersActive', 'members_active')),
    unmatchedLeads: num(pick(r, 'unmatchedLeads', 'unmatched_leads')),
  };
}

/**
 * "Segmento "X": N membro(s) · M lead(s) sem contato ligado" — a segunda
 * metade só aparece quando há pelo menos 1 (contrato §6): "· 0" ao lado de
 * um número seria ruído, e omitir SEMPRE esconderia o caso que a frase
 * existe para avisar.
 */
export function fraseDoFiltroDeSegmento(s: SegmentFilterResult): string {
  const membros = s.membersActive === null ? '—' : s.membersActive.toLocaleString('pt-BR');
  const base = `Segmento "${s.name}": ${membros} membro(s)`;
  if (s.unmatchedLeads !== null && s.unmatchedLeads > 0) {
    return `${base} · ${s.unmatchedLeads.toLocaleString('pt-BR')} lead(s) sem contato ligado`;
  }
  return base;
}
